/**
 * MCPTTProvider — React context wrapping a single MCPTTClient instance.
 *
 * One client per app session. The provider holds it, plumbs lifecycle
 * events into a small `Snapshot` state object, and exposes both:
 *   - the raw client (for actions: login, PTT, ack, etc.)
 *   - reactive snapshots (for components to render off)
 *
 * Hooks built on top of this context live in `./hooks.ts`.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  MCPTTClient,
  WebAudioAdapter,
  type AudioState,
  type Call,
  type Codec,
  type ConnectionState,
  type EmergencyMessage,
  type Network,
} from "@mcptt/client";

import { APP_NAME, APP_VERSION, SERVER_ADDRESS } from "../config";

/** Reactive snapshot mirrored from the client. Components subscribe via hooks. */
export interface MCPTTSnapshot {
  state:      ConnectionState;
  userId:     string | null;
  /** Frozen reference to client.networks; updated on every coarse change. */
  networks:   ReadonlyArray<Network>;
  calls:      ReadonlyArray<Call>;
  /** Active (un-resolved) emergencies, keyed by EmergencyID. */
  emergencies: ReadonlyMap<string, EmergencyMessage>;
  /** Last surfaced auth-error message — set on bad login, cleared on retry. */
  authError:  string | null;
  /** Audio subsystem state. "off" until the operator clicks Enable Audio. */
  audioState:  AudioState;
  /** Last audio error (mic denied, context init failed, etc.). */
  audioError:  string | null;
  /** Currently-selected audio codec (mulaw default). */
  audioCodec:  Codec;
  /** RX master volume in [0, 1]. */
  audioVolume: number;
  /** True iff RX is muted (volume is preserved). */
  audioMuted:  boolean;
}

/** Action surface exposed by the provider. */
export interface MCPTTApi {
  client:     MCPTTClient;
  snapshot:   MCPTTSnapshot;
  /** Connect + login in one step. Resolves on END_INITIALIZE. */
  signIn:     (login: string, password: string) => Promise<void>;
  /** Tear down the WS and reset snapshot. */
  signOut:    () => void;
  /** Acknowledge an active emergency (NetworkID is required by the wire). */
  ackEmergency: (emergencyId: string, networkId: string) => void;
  /** Operator-driven: spin up AudioContext + preflight mic. MUST be
   *  called from a user-gesture handler. */
  enableAudio:  () => Promise<void>;
  /** Tear down the audio subsystem. */
  disableAudio: () => Promise<void>;
  /** Toggle codec at runtime. RX adapts on the next frame. */
  setAudioCodec: (codec: Codec) => void;
  /** Set master RX volume (clamped to [0, 1]). */
  setAudioVolume: (v: number) => void;
  /** Mute / un-mute RX. Volume is preserved across mute toggles. */
  setAudioMuted: (muted: boolean) => void;
  /** Read live RX level [0..1] — call from a requestAnimationFrame
   *  loop in your meter component. Returns 0 when audio is off. */
  getRxLevel: () => number;
  /** Read live TX (mic) level [0..1]. */
  getTxLevel: () => number;
}

const MCPTTCtx = createContext<MCPTTApi | null>(null);

export function MCPTTProvider({ children }: { children: ReactNode }) {
  // One client + one audio adapter for the lifetime of the provider.
  // The adapter starts in state "off" and is wired into the client up
  // front; the operator activates it from the AppBar via enableAudio().
  // This keeps RX playback automatic once enabled and avoids any
  // re-construction churn when audio toggles on/off.
  const clientRef = useRef<MCPTTClient | null>(null);
  const audioRef  = useRef<WebAudioAdapter | null>(null);
  if (!clientRef.current) {
    audioRef.current = new WebAudioAdapter({
      // Captured FleetConsole packets for this manager show long runs
      // of 0xD5 during silence, which is the G.711 A-law silence byte.
      // Default to A-law for Echo911; µ-law remains selectable in the
      // audio dropdown for other managers.
      codec: "alaw",
      log: (...args) => { /* eslint-disable-next-line no-console */ console.debug("[mcptt-audio]", ...args); },
    });
    clientRef.current = new MCPTTClient({
      serverAddress: SERVER_ADDRESS,
      appName:       APP_NAME,
      appVersion:    APP_VERSION,
      autoReconnect: true,
      audio:         audioRef.current,
      debug:         false,
    });
  }
  const client = clientRef.current;
  const audio  = audioRef.current!;

  const [snapshot, setSnapshot] = useState<MCPTTSnapshot>(() => ({
    state:       client.state,
    userId:      client.userId,
    networks:    client.networks,
    calls:       client.calls,
    emergencies: new Map(),
    authError:   null,
    audioState:  audio.state(),
    audioError:  audio.lastError(),
    audioCodec:  audio.getCodec(),
    audioVolume: audio.getVolume(),
    audioMuted:  audio.isMuted(),
  }));

  // Local mutable refs that we re-snapshot on each event.
  const emergenciesRef = useRef<Map<string, EmergencyMessage>>(new Map());

  const refreshSnapshot = useCallback((patch?: Partial<MCPTTSnapshot>) => {
    setSnapshot((prev) => ({
      state:       client.state,
      userId:      client.userId,
      networks:    client.networks,
      calls:       client.calls,
      emergencies: new Map(emergenciesRef.current),
      authError:   prev.authError,
      audioState:  audio.state(),
      audioError:  audio.lastError(),
      audioCodec:  audio.getCodec(),
      audioVolume: audio.getVolume(),
      audioMuted:  audio.isMuted(),
      ...(patch ?? {}),
    }));
  }, [client, audio]);

  // Audio adapter state changes also drive the snapshot — the AppBar
  // pill flips green/amber/red as state changes.
  useEffect(() => {
    audio.onState = () => refreshSnapshot();
    return () => { if (audio.onState) audio.onState = null; };
  }, [audio, refreshSnapshot]);

  // Feed the server-advertised audio sample rate into the adapter.
  // Fleet Server 5.8 commonly reports 16000 Hz; using the old 8000 Hz
  // assumption plays RX at the wrong speed/pitch.
  useEffect(() => {
    const rate = client.audioConfig.sampleRate;
    if (rate) audio.setSourceSampleRate(rate);
  }, [client, audio, snapshot.state]);

  // Wire client events into snapshot updates.
  useEffect(() => {
    const offState     = client.on("state",     () => refreshSnapshot());
    const offNetworks  = client.on("networks",  () => refreshSnapshot());
    const offReady     = client.on("ready",     () => refreshSnapshot());
    const offPtt       = client.on("ptt",       () => refreshSnapshot());
    const offRxStart   = client.on("rx:start",  () => refreshSnapshot());
    const offRxStop    = client.on("rx:stop",   () => refreshSnapshot());
    // Device GPS / parameter pushes don't currently re-emit "networks",
    // so subscribe explicitly. Without these the map and roster lag
    // until the next coarse network update.
    const offGps       = client.on("device:gps",    () => refreshSnapshot());
    const offDevChg    = client.on("device:change", () => refreshSnapshot());

    const offAuthError = client.on("auth-error", (_code, message) => {
      refreshSnapshot({ authError: message });
    });

    const offEmergency = client.on("emergency", (msg) => {
      emergenciesRef.current.set(msg.EmergencyID, msg);
      refreshSnapshot();
    });
    const offEmergencyAck = client.on("emergency:ack", () => {
      // We keep the emergency on screen until the operator clears it
      // explicitly; a server-pushed ACK just means somebody (maybe us)
      // responded. UI badge will show who from snapshot.calls/log.
      refreshSnapshot();
    });

    const offDisconnect = client.on("disconnect", () => refreshSnapshot());
    const offTransport  = client.on("transport-error", () => refreshSnapshot());

    return () => {
      offState();
      offNetworks();
      offReady();
      offPtt();
      offRxStart();
      offRxStop();
      offGps();
      offDevChg();
      offAuthError();
      offEmergency();
      offEmergencyAck();
      offDisconnect();
      offTransport();
    };
  }, [client, refreshSnapshot]);

  // Cleanly disconnect on unmount.
  useEffect(() => {
    return () => { client.disconnect(); };
  }, [client]);

  const signIn = useCallback(async (login: string, password: string) => {
    setSnapshot((s) => ({ ...s, authError: null }));
    // login() opens the transport on its own if needed and drives the
    // full SERVER_CONFIG / DEVICE_CONFIG / LOGIN handshake.
    await client.login(login, password);
  }, [client]);

  const signOut = useCallback(() => {
    client.disconnect();
    emergenciesRef.current.clear();
    refreshSnapshot({ authError: null });
  }, [client, refreshSnapshot]);

  const ackEmergency = useCallback((emergencyId: string, networkId: string) => {
    client.ackEmergency({ emergencyId, networkId });
    emergenciesRef.current.delete(emergencyId);
    refreshSnapshot();
  }, [client, refreshSnapshot]);

  const enableAudio = useCallback(async () => {
    await audio.enable();
    refreshSnapshot();
  }, [audio, refreshSnapshot]);

  const disableAudio = useCallback(async () => {
    await audio.disable();
    refreshSnapshot();
  }, [audio, refreshSnapshot]);

  const setAudioCodec = useCallback((codec: Codec) => {
    audio.setCodec(codec);
    refreshSnapshot();
  }, [audio, refreshSnapshot]);

  const setAudioVolume = useCallback((v: number) => {
    audio.setVolume(v);
    refreshSnapshot();
  }, [audio, refreshSnapshot]);

  const setAudioMuted = useCallback((muted: boolean) => {
    audio.setMuted(muted);
    refreshSnapshot();
  }, [audio, refreshSnapshot]);

  // The level meters intentionally bypass React state — they're
  // polled by the meter component's rAF loop via these stable refs.
  const getRxLevel = useCallback(() => audio.getRxLevel(), [audio]);
  const getTxLevel = useCallback(() => audio.getTxLevel(), [audio]);

  const api = useMemo<MCPTTApi>(() => ({
    client,
    snapshot,
    signIn,
    signOut,
    ackEmergency,
    enableAudio,
    disableAudio,
    setAudioCodec,
    setAudioVolume,
    setAudioMuted,
    getRxLevel,
    getTxLevel,
  }), [client, snapshot, signIn, signOut, ackEmergency, enableAudio,
       disableAudio, setAudioCodec, setAudioVolume, setAudioMuted,
       getRxLevel, getTxLevel]);

  return <MCPTTCtx.Provider value={api}>{children}</MCPTTCtx.Provider>;
}

export { MCPTTCtx };
